import React, { useEffect, useState } from 'react';
import { S3FieldsType } from './StorageTypeFields';
import { Typography } from '@mui/material';
import BucketIcon from '@mui/icons-material/Inventory2';
import KeyIcon from '@mui/icons-material/VpnKey';
import PasswordIcon from '@mui/icons-material/Password';
import LinkIcon from '@mui/icons-material/Link';
import LocationOnIcon from '@mui/icons-material/LocationOn';

interface S3FieldsProps extends S3FieldsType {
  validationErrors: {
    bucket?: string;
    accessKey?: string;
    secret?: string;
    region?: string;
    endpoint?: string;
  };
  onChange: (field: string, value: string) => void;
  isEdit?: boolean;
}

const S3Fields: React.FC<S3FieldsProps> = ({
  bucket,
  accessKey,
  secret,
  endpoint,
  region,
  validationErrors,
  onChange,
  isEdit = false,
}) => {
  const [isS3Compatible, setIsS3Compatible] = useState(false);

  useEffect(() => {
    const selectedProvider = localStorage.getItem('selectedStorageType');
    // S3-compatible providers: AKAVE, NAMI, OTHER (not native AWS S3)
    const isCompatible = selectedProvider === 'AKAVE' || selectedProvider === 'NAMI' || selectedProvider === 'OTHER';
    setIsS3Compatible(isCompatible);
  }, []);
  return (
    <div className="space-y-5 pt-4 border-t border-gray-100">
      <h4 className="text-[13px] font-semibold text-[#181D27] uppercase tracking-wide">
        S3 Configuration
      </h4>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Bucket */}
        <div>
          <label className="block text-[13px] font-medium text-[#535862] mb-1.5">
            Bucket <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={bucket}
            onChange={(e) => onChange('bucket', e.target.value)}
            placeholder="my-bucket-s3"
            className={`block w-full rounded-lg border ${
              validationErrors.bucket ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-200 focus:border-[#617BFF] focus:ring-[#617BFF]'
            } bg-white px-3.5 py-2.5 text-[14px] text-[#181D27] placeholder-gray-400 focus:ring-1 transition-colors`}
          />
          {validationErrors.bucket && (
            <p className="mt-1.5 text-[13px] text-red-500">{validationErrors.bucket}</p>
          )}
        </div>

        {/* Access Key */}
        <div>
          <label className="block text-[13px] font-medium text-[#535862] mb-1.5">
            Access Key <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={accessKey}
            onChange={(e) => onChange('accessKey', e.target.value)}
            placeholder="AKIAXXXXXXXXXXXXXXXX"
            className={`block w-full rounded-lg border ${
              validationErrors.accessKey ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-200 focus:border-[#617BFF] focus:ring-[#617BFF]'
            } bg-white px-3.5 py-2.5 text-[14px] text-[#181D27] placeholder-gray-400 focus:ring-1 transition-colors`}
          />
          {validationErrors.accessKey && (
            <p className="mt-1.5 text-[13px] text-red-500">{validationErrors.accessKey}</p>
          )}
        </div>

        {/* Secret Key */}
        <div>
          <label className="block text-[13px] font-medium text-[#535862] mb-1.5">
            Secret Key {!isEdit && <span className="text-red-500">*</span>}
          </label>
          <input
            type="password"
            value={secret}
            onChange={(e) => onChange('secret', e.target.value)}
            placeholder="••••••••••••••••••••"
            className={`block w-full rounded-lg border ${
              validationErrors.secret ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-200 focus:border-[#617BFF] focus:ring-[#617BFF]'
            } bg-white px-3.5 py-2.5 text-[14px] text-[#181D27] placeholder-gray-400 focus:ring-1 transition-colors`}
          />
          {validationErrors.secret && (
            <p className="mt-1.5 text-[13px] text-red-500">{validationErrors.secret}</p>
          )}
        </div>

        {/* Region */}
        <div>
          <label className="block text-[13px] font-medium text-[#535862] mb-1.5">Region</label>
          <input
            type="text"
            value={region}
            onChange={(e) => onChange('region', e.target.value)}
            placeholder="eu-west-3"
            className={`block w-full rounded-lg border ${
              validationErrors.region ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-200 focus:border-[#617BFF] focus:ring-[#617BFF]'
            } bg-white px-3.5 py-2.5 text-[14px] text-[#181D27] placeholder-gray-400 focus:ring-1 transition-colors`}
          />
          {validationErrors.region && (
            <p className="mt-1.5 text-[13px] text-red-500">{validationErrors.region}</p>
          )}
        </div>
      </div>

      {/* Endpoint field: hidden for AWS, shown and required for S3-compatible providers */}
      {isS3Compatible && (
        <div>
          <label className="block text-[13px] font-medium text-[#535862] mb-1.5">
            Endpoint <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={endpoint}
            onChange={(e) => onChange('endpoint', e.target.value)}
            placeholder="https://s3.example.com"
            className={`block w-full rounded-lg border ${
              validationErrors.endpoint ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-200 focus:border-[#617BFF] focus:ring-[#617BFF]'
            } bg-white px-3.5 py-2.5 text-[14px] text-[#181D27] placeholder-gray-400 focus:ring-1 transition-colors`}
          />
          {validationErrors.endpoint && (
            <p className="mt-1.5 text-[13px] text-red-500">{validationErrors.endpoint}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default S3Fields;
